import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import AgentsList from './AgentsList';
import AgentDetails from './AgentDetails';
import AgentFormDialog from './AgentFormDialog';
import AgentCreationModeDialog from './AgentCreationModeDialog';
import TemplateSelectionDialog from './TemplateSelectionDialog';
import DuplicateAgentDialog from './DuplicateAgentDialog';
import { Agent, AgentListQuery } from '../../types/agent';
import { agentApi } from '../../utils/agentApi';
import toast from 'react-hot-toast';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useTranslation } from 'react-i18next';

const AgentsManagement: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canCreateAgents } = usePermissions();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [creationModeOpen, setCreationModeOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [editingAgentId, setEditingAgentId] = useState<string | undefined>(undefined);
  const [initialAgentData, setInitialAgentData] = useState<any | undefined>(undefined);
  const [detailsRefreshKey, setDetailsRefreshKey] = useState(0);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const query: AgentListQuery = {
        pageIndex: 1,
        pageSize: 100,
        agentName: '',
        agentStatus: ''
      };

      const agentsData = await agentApi.getAgentsList(query);
      setAgents(agentsData);
      setFilteredAgents(agentsData);
    } catch (error) {
      console.error('Error loading agents:', error);
      toast.error(t('agents.errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredAgents(agents);
    } else {
      const query = searchText.toLowerCase();
      const filtered = agents.filter(agent => {
        const name = (agent.name || '').toLowerCase();
        const description = (agent.description || '').toLowerCase();
        return name.includes(query) || description.includes(query);
      });
      setFilteredAgents(filtered);
    }
  }, [searchText, agents]);

  const handleCreateAgent = () => {
    setEditingAgentId(undefined);
    setInitialAgentData(undefined);
    setCreationModeOpen(true);
  };

  const handleManageWithAI = () => {
    setCreationModeOpen(false);
    navigate('/admin/');
  };

  const handleAgentCreated = (newAgent: Agent) => {
    setAgents(prev => [...prev, newAgent]);
    toast.success(t('agents.notifications.created'));
    setFormDialogOpen(false);
    setDetailsRefreshKey((k) => k + 1);
  };

  const handleAgentUpdated = (updatedAgent: Agent) => {
    setAgents(prev =>
      prev.map(agent =>
        agent.id === updatedAgent.id ? updatedAgent : agent
      )
    );
    toast.success(t('agents.notifications.updated'));
    setDetailsRefreshKey((k) => k + 1);
  };

  const handleAgentDeleted = (deletedAgentId: string) => {
    setAgents(prev => prev.filter(agent => agent.id !== deletedAgentId));
    toast.success(t('agents.notifications.deleted'));
    navigate('/admin/agents');
  };

  const handleDuplicateAgent = async (agentId: string) => {
    try {
      const duplicatedAgent = await agentApi.duplicateAgent(agentId);
      setAgents(prev => [...prev, duplicatedAgent]);
      toast.success(t('agents.notifications.duplicated'));
    } catch (error) {
      console.error('Error duplicating agent:', error);
      toast.error(t('agents.errors.duplicateError'));
    }
  };

  return (
    <Box>
      <Routes>
        <Route
          path="/"
          element={
            <Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
              }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                  {t('agents.title')}
                </Typography>

                {canCreateAgents && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateAgent}
                    size="large"
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#000' : undefined
                    }}
                  >
                    {t('agents.create')}
                  </Button>
                )}
              </Box>

              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder={t('agents.searchPlaceholder')}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <Typography variant="body2" color="text.secondary">
                  {t('agents.searchResults', { count: filteredAgents.length })}
                </Typography>
              </Paper>

              <AgentsList
                agents={filteredAgents}
                loading={loading}
                onEdit={(agentId) => { setEditingAgentId(agentId); setFormDialogOpen(true); }}
                onView={(agentId) => navigate(`/admin/agents/${agentId}`)}
                onDuplicate={handleDuplicateAgent}
                onDelete={handleAgentDeleted}
                onRefresh={loadAgents}
              />

            </Box>
          }
        />

        {/* Edit/Create handled via modal; keep details route only */}

        <Route
          path="/:agentId"
          element={
            <AgentDetails
              onEdit={(agentId) => { setEditingAgentId(agentId); setFormDialogOpen(true); }}
              onDelete={handleAgentDeleted}
              onDuplicate={handleDuplicateAgent}
              onBack={() => navigate('/admin/agents')}
              refreshKey={detailsRefreshKey}
            />
          }
        />
      </Routes>

      <AgentCreationModeDialog
        open={creationModeOpen}
        onClose={() => setCreationModeOpen(false)}
        onCreateFromScratch={() => {
          setCreationModeOpen(false);
          setInitialAgentData(undefined);
          setFormDialogOpen(true);
        }}
        onChooseTemplate={() => {
          setCreationModeOpen(false);
          setTemplateDialogOpen(true);
        }}
        onDuplicateFromAgent={() => {
          setCreationModeOpen(false);
          setDuplicateDialogOpen(true);
        }}
        onManageWithAI={handleManageWithAI}
      />

      <TemplateSelectionDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onSelectTemplate={(tpl) => {
          setTemplateDialogOpen(false);
          setInitialAgentData(tpl);
          setEditingAgentId(undefined);
          setFormDialogOpen(true);
        }}
      />

      <DuplicateAgentDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        agents={agents}
        onSelectAgentToDuplicate={async (agentId) => {
          try {
            setDuplicateDialogOpen(false);
            const base = await agentApi.getAgent(agentId);
            const { id, createdAt, updatedAt, ...rest } = base as any;
            setInitialAgentData({ ...rest, name: `${base.name} ${t('agents.copySuffix')}` });
            setEditingAgentId(undefined);
            setFormDialogOpen(true);
          } catch (error) {
            console.error('Error preparing duplicate data:', error);
            toast.error(t('agents.errors.prepareDuplicateError'));
          }
        }}
      />

      <AgentFormDialog
        open={formDialogOpen}
        agentId={editingAgentId}
        initialData={initialAgentData}
        onClose={() => setFormDialogOpen(false)}
        onSaved={(agent) => {
          if (editingAgentId) {
            handleAgentUpdated(agent);
          } else {
            handleAgentCreated(agent);
          }
        }}
      />
    </Box>
  );
};

export default AgentsManagement;